import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IAnswerDeviceRequest } from "../models/Devices/answerDeviceRequest";
import { CommunicationMode, IDevice } from "../models/Devices/device";
import { IDeviceApiResponse } from "../models/api/deviceApiResponse";
import { IDevicePropertyApiResponse } from "../models/api/devicePropertyApiResponse";
import { IDurationDeviceRequest } from "../models/Devices/durationDeviceRequest";
import { IParameter } from "../models/parameter";
import DeviceServices from "../services/DeviceService";
import { IDeviceFilter } from "../models/filters/deviceFilter";
import IDeviceForm from "../models/forms/deviceForm";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import DeviceForm from "../models/forms/deviceForm";
import DevicePropertyForm from "../models/forms/devicePropertyForm";
import { IManufactoringProductionPDTO } from "../models/dtos/manufactoringProductionPDTO";
import { useUsersStore } from "./users.store";
import { useStartupStore } from "./startup.store";
import { IFTPHeterospermyRequestInfo } from "../models/Devices/ftpHeterospermyRequestInfo";
import { useCenterStore } from "./center.store";
import { useParameterStore } from "./parameter.store";
import { IDeviceProperty } from "../models/Devices/deviceProperty";
import i18n from "@/i18n";

//Store Pinia Devices
export const useDeviceStore = defineStore("devices", () => {
    // State
    const types = ref<IParameter[]>([]);
    const datas = ref<IDevice[]>([]);
    const current = ref<IDevice>({} as IDevice);
    const itemForm = ref<IDeviceForm>({} as IDeviceForm);

    // Actions - Hydration
    /**
     * Hydrate Devices from API response
     */
    async function hydrateDevices(devices: IDeviceApiResponse[]): Promise<IDevice[]> {
        if (devices.length === 0) return [];

        // 1. Get stores
        const centerStore = useCenterStore();
        const parameterStore = useParameterStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: IDevice[] = [];
        for (const device of devices) {
            const model = { ...device } as unknown as IDevice;

            // Link references (O(1) via Map)
            model.center = centerMap.get(device.centerId) ?? model.center;
            model.type = parameterMap.get(device.typeId) ?? model.type;

            // Hydrate child properties
            if (device.properties && device.properties.length > 0) {
                model.properties = device.properties.map(prop => ({
                    ...prop,
                    deviceId: device.id
                } as unknown as IDeviceProperty));
            } else {
                model.properties = [];
            }

            models.push(model);
        }

        return models;
    }

    // Getters
    const getDeviceById = computed(() => {
        return (id: number): IDevice | undefined => {
            return datas.value.find((device) => device.id == id);
        };
    });

    const getDeviceByType = computed(() => {
        return (typeId: number): IDevice[] => {
            // Use typeId instead of type.id to avoid undefined access
            return datas.value.filter((device) => device.typeId == typeId);
        };
    });

    const getDeviceByTypeForCurrentCenter = computed(() => {
        return (typeId: number): IDevice[] => {
            const userStore = useUsersStore();
            // Use typeId and centerId instead of navigation properties
            return datas.value.filter((device) => device.typeId == typeId && device.centerId == userStore.currentCenter.id);
        };
    });

    const getDevicesForCurrentCenter = computed(() => {
        return (): IDevice[] => {
            const userStore = useUsersStore();
            // Use centerId instead of center.id to avoid undefined access
            return datas.value.filter(d => d.centerId == userStore.currentCenter.id);
        };
    });

    const getDeviceMode = computed(() => {
        return (deviceId: number): CommunicationMode => {
            const device = datas.value.filter((device) => device.id == deviceId);
            if (device) {
                const property = device[0].properties.find((property) => property.key == "MODE");
                if (property && property.value != '') {
                    return Number(property.value) as CommunicationMode;
                }
            }
            return CommunicationMode.None;
        };
    });

    const getProperty = computed(() => {
        return (key: string, add?: boolean, value?: string): DevicePropertyForm | undefined => {
            let property = itemForm.value.properties.find((property) => property.key == key);
            if (!property && add) {
                property = new DevicePropertyForm;
                property.key = key;
                property.value = value ?? '';
                property.deviceId = itemForm.value.id;
                itemForm.value.properties.push(property);
            }
            return property;
        };
    });

    // Actions
    async function loadTypes() {
        let result = await DeviceServices.pathParamApiListCall<IParameter>(["types"]);
        types.value = [];

        if (!result.error) {
            types.value = result.data as IParameter[];
        }

        return result;
    }

    async function load(filter: IDeviceFilter) {
        const startupStore = useStartupStore();
        if (startupStore.isMainInitializing) {
            let result = await DeviceServices.getAll<IDeviceFilter, IDeviceApiResponse>(filter);
            datas.value = [];

            if (!result.error) {
                datas.value = await hydrateDevices(result.data as IDeviceApiResponse[]);
            }

            return result;
        }
        else {
            console.error("Store loading aborted : triggered from outside main.ts context");
        }
    }

    async function registerMeasurementRequest(deviceId: number) {
        await DeviceServices.pathParamApiCall(["measurement", "register", `${deviceId}`], "post");
    }

    function registerMeasurementBeaconRequest(deviceId: number) {
        DeviceServices.sendBeacon(`measurement/register/${deviceId}`);
    }

    async function cancelMeasurementRequest(deviceId: number) {
        await DeviceServices.pathParamApiCall(["measurement", "cancel", `${deviceId}`], "post");
    }

    function cancelMeasurementBeaconRequest(deviceId: number) {
        DeviceServices.sendBeacon(`measurement/cancel/${deviceId}`);
    }

    async function sendMeasurementInstructions(deviceId: number, deviceRequest: IAnswerDeviceRequest) {
        await DeviceServices.pathParamApiCall(["measurement", "instructions", `${deviceId}`], "post", deviceRequest);
    }

    async function sendMonospermyBytesToFTPDevice(deviceId: number, manufactureList: IManufactoringProductionPDTO[], isDailyBase: boolean) {
        if (manufactureList.length == 0) {
            console.warn(`Unable to send instructions to device ${deviceId} : manufactureProduction list is empty`);
            return;
        }
        await DeviceServices.pathParamApiCall(["measurement", "ftp", "mono", `${deviceId}`, `${isDailyBase ? '1' : '0'}`], "post", manufactureList);
    }

    async function sendHeterospermyBytesToFTPDevice(deviceId: number, requestInfo: IFTPHeterospermyRequestInfo[]) {
        if (requestInfo.length == 0) {
            console.warn(`Unable to send instructions to device ${deviceId} : no relevant lots`);
            return;
        }
        await DeviceServices.pathParamApiCall(["measurement", "ftp", "hetero", `${deviceId}`], "post", requestInfo);
    }

    async function sendDurationMeasurementInstructions(deviceId: number, deviceRequest: IDurationDeviceRequest) {
        await DeviceServices.pathParamApiCall(["measurement", "duration", `${deviceId}`], "post", deviceRequest);
    }

    async function get(id: number) {
        let result = await DeviceServices.getById<IDeviceApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateDevices([result.data as IDeviceApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number): Promise<IDevice> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IDevice | IDataOperationResult<IDevice>>;
        if (itemForm.value.id > 0) {
            result = await DeviceServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDevices([result.data as IDeviceApiResponse]);
                current.value = modelData;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IDevice>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await DeviceServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDevices([result.data as IDeviceApiResponse]);
                current.value = modelData;
                datas.value.push(current.value);
            }
        }
        return result;
    }

    function toForm(item: IDevice): DeviceForm {
        const form = new DeviceForm;
        form.id = item.id;
        form.centerId = item.centerId;
        form.name = item.name;
        form.typeId = item.typeId;
        item.properties.forEach((property) => {
            form.properties.push({ key: property.key, value: property.value, id: property.id, deviceId: item.id } as DevicePropertyForm);
        });
        return form;
    }

    function newForm(lienId: number): IDeviceForm {
        itemForm.value = {
            id: 0,
            name: "",
            typeId: 0,
            centerId: lienId,
            properties: []
        } as IDeviceForm;
        return itemForm.value;
    }

    function newItem(lien: ICenter): IDevice {
        current.value = {
            id: 0,
            name: "",
            type: { id: 0 } as IParameter,
            typeId: 0,
            properties: [],
            center: lien,
            centerId: lien.id,
            addingDate: new Date(),
            active: true
        } as IDevice;
        return current.value;
    }

    return {
        // State
        types,
        datas,
        current,
        itemForm,
        // Getters
        getDeviceById,
        getDeviceByType,
        getDeviceByTypeForCurrentCenter,
        getDevicesForCurrentCenter,
        getDeviceMode,
        getProperty,
        // Actions
        loadTypes,
        load,
        registerMeasurementRequest,
        registerMeasurementBeaconRequest,
        cancelMeasurementRequest,
        cancelMeasurementBeaconRequest,
        sendMeasurementInstructions,
        sendMonospermyBytesToFTPDevice,
        sendHeterospermyBytesToFTPDevice,
        sendDurationMeasurementInstructions,
        get,
        searchStoreById,
        save,
        toForm,
        newForm,
        newItem
    };
});
